import React, { memo, useCallback, useMemo, useState } from 'react'

import { classnames, CubeCheck, Icon, MoreChorizontal } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import { iconSize, VARIATION_TYPES } from 'Pods/ThreeDStudio/constants'
import { buildSharedRunPathname } from 'Pods/ThreeDStudio/Hooks/buildSharedRunPathname'
import DeleteConfirmModal from 'Pods/ThreeDStudio/Shared/DeleteConfirmModal/DeleteConfirmModal'
import Dropdown from 'Pods/ThreeDStudio/Shared/Dropdown/Dropdown'
import { threeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setOpenedModel } from 'Pods/ThreeDStudio/Store/actions/ui'

import { NOOP } from 'Utils/stableDefaults'

import Menu from './Components/Menu/Menu'

const triggerIconSize = { height: '36px', width: '36px' }
const triggerIcon = <Icon notSpaced svgComponent={MoreChorizontal} color="dark" size={triggerIconSize} />

function Card({
  variation,
  position = null,
  isActive = false,
  isMain = false,
  isActionLoading = false,
  onSetAsMain: handleSetAsMain = NOOP,
  onDelete: handleDelete = NOOP,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const [shouldCloseDropdown, setShouldCloseDropdown] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const { id, slug, providerModelId, outputAssets, title, prompt, createdAt } = variation
  const previewUrl = outputAssets?.[0]?.previewUrl
  const variationType = VARIATION_TYPES[providerModelId]
  const itemName = variationType?.label ?? title ?? prompt

  const handleClick = useCallback(() => {
    const targetPathname = buildSharedRunPathname({ id, slug, title: title ?? prompt })
    if (location?.pathname !== targetPathname) {
      navigate(targetPathname, { state: { keepVariationsTab: true } })
      return
    }
    // Same URL, so no navigation (and no useModelUrlSync re-open) fires. This
    // happens when the uploaded-source card replaced this run in the viewer;
    // re-open the run from the store to load it back.
    const { ui, modelSettings } = threeDStudioStore.getState()
    if (modelSettings?.inputThreeDUrl && ui?.openedModel?.id != null) {
      setOpenedModel(ui.openedModel, { keepVariationsTab: true })
    }
  }, [navigate, location, id, slug, title, prompt])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }, [handleClick])

  const handleSetAsMainClick = useCallback(() => {
    setShouldCloseDropdown(true)
    handleSetAsMain(id, itemName)
  }, [handleSetAsMain, id, itemName])

  const handleOpenDelete = useCallback(() => {
    setShouldCloseDropdown(true)
    setIsDeleteModalOpen(true)
  }, [])

  const handleCloseDelete = useCallback(() => {
    setIsDeleteModalOpen(false)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    handleDelete(id, itemName)
    setIsDeleteModalOpen(false)
  }, [handleDelete, id, itemName])

  const menuContent = useMemo(() =>
    <Menu
      createdAt={createdAt}
      onSetAsMain={handleSetAsMainClick}
      onDelete={handleOpenDelete}
      isDisabled={isActionLoading}
    />,
    [createdAt, handleSetAsMainClick, handleOpenDelete, isActionLoading],
  )

  return (
    <>
      <div
        className={classnames(
          'variation-card',
          isActive && 'variation-card--active',
          isMenuOpen && 'variation-card--menu-open',
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        {previewUrl && (
          <img
            className="variation-card__image"
            src={previewUrl}
            alt={title ?? prompt ?? variationType?.label ?? 'Variation preview'}
          />
        )}
        {variationType && (
          <span className="variation-card__type cgt-caption-semibold">
            <Icon notSpaced svgComponent={variationType.icon} color="dark" size={iconSize} />
            {variationType.label}
          </span>
        )}
        {isMain && (
          <span className="variation-card__main-badge" aria-label="Main variation">
            <Icon notSpaced svgComponent={CubeCheck} color="dark" size={iconSize} />
          </span>
        )}
        {position != null && <span className="variation-card__position">{position}</span>}
        <Dropdown
          icon={triggerIcon}
          content={menuContent}
          placement="bottom-start"
          className="variation-card__dropdown-trigger"
          contentClassName="variation-card__dropdown-content"
          triggerCloseDropdown={shouldCloseDropdown}
          setTriggerCloseDropdown={setShouldCloseDropdown}
          onOpenChange={setIsMenuOpen}
          usePortal
        />
      </div>
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          itemType="variation"
          itemName={itemName}
          isSubmitDisabled={isActionLoading}
          onSubmit={handleConfirmDelete}
          onCancel={handleCloseDelete}
        />
      )}
    </>
  )
}

Card.propTypes = {
  variation: shape({
    id: oneOfType([string, number]).isRequired,
    slug: string,
    providerModelId: string,
    outputAssets: arrayOf(shape({
      previewUrl: string,
    })),
    title: string,
    prompt: string,
    createdAt: string,
  }).isRequired,
  position: number,
  isActive: bool,
  isMain: bool,
  isActionLoading: bool,
  onSetAsMain: func,
  onDelete: func,
}

export default memo(Card)
